import { useState, useCallback, useEffect } from 'react';
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Chip, TextField, MenuItem, IconButton,
  Tooltip, Stepper, Step, StepLabel, StepContent, Divider,
  Stack, Autocomplete, CircularProgress,
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  AddCircleOutline as AddIcon,
  Delete as DeleteIcon,
  LocalShipping as ShippingIcon,
  Place as PlaceIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import CustomPopup from '../../components/CustomPopup/CustomPopup';
import Table, { Column } from '../../components/Table/Table';
import { useAlert } from '../../contexts/AlertContext';
import { useTranslation } from 'react-i18next';
import { useServerSidePagination } from '../../hooks/useServerSidePagination';
import { PaginatedResponse } from '../../types/api';
import {
  shipmentsApi,
  ShipmentListItem,
  ShipmentApiResponse,
  TrackingEvent,
  CreateShipmentRequest,
  CreateTrackingEventRequest,
} from '../../services/shipmentsApi';
import { documentsApi, DocumentApiResponse } from '../../services/documentsApi';
import { getAutocompleteStyles, getAutocompleteListboxStyles } from '../../styles/formStyles';

interface ShipmentFormData {
  sender: string;
  receiver: string;
  status: string;
  weight: number | string;
  carrier: string;
  carrier_tracking_number: string;
  document_id: number | null;
  notes: string;
}

const emptyForm: ShipmentFormData = {
  sender: '',
  receiver: '',
  status: 'w_magazynie',
  weight: '',
  carrier: 'manual',
  carrier_tracking_number: '',
  document_id: null,
  notes: '',
};

const statusColorMap: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  'w_magazynie': 'info',
  'w_drodze': 'warning',
  'dostarczona': 'success',
  'anulowana': 'error',
};

const Shipments = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<ShipmentFormData>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentListItem | null>(null);

  // WZ documents for selector
  const [wzDocuments, setWzDocuments] = useState<DocumentApiResponse[]>([]);
  const [wzLoading, setWzLoading] = useState(false);

  // Tracking detail dialog
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false);
  const [trackingShipment, setTrackingShipment] = useState<ShipmentApiResponse | null>(null);
  const [wzDocumentNumber, setWzDocumentNumber] = useState<string | null>(null);

  // Add tracking event dialog
  const [addEventDialogOpen, setAddEventDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState<CreateTrackingEventRequest>({
    description: '',
    location: '',
    status: '',
  });

  const loadWzDocuments = async () => {
    setWzLoading(true);
    try {
      const resp = await documentsApi.getDocuments({ type: 'WZ', page_size: 100 });
      setWzDocuments(resp.results);
    } catch {
      // silent - not critical
    } finally {
      setWzLoading(false);
    }
  };

  useEffect(() => {
    if (isDialogOpen) {
      loadWzDocuments();
    }
  }, [isDialogOpen]);

  const fetchShipments = useCallback(
    async (page: number, pageSize: number, search: string): Promise<PaginatedResponse<ShipmentListItem>> => {
      return shipmentsApi.getShipments({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
      });
    },
    []
  );

  const pagination = useServerSidePagination(fetchShipments, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (error: any) => {
      const msg = error.response?.data?.detail || error.message || t('shipments.fetchError');
      showAlert('error', msg, 5000);
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: ShipmentFormData) => {
      const request: CreateShipmentRequest = {
        sender: data.sender,
        receiver: data.receiver,
        status: data.status || 'w_magazynie',
        weight: Number(data.weight) || 0,
        carrier: data.carrier || 'manual',
        carrier_tracking_number: data.carrier_tracking_number || '',
        document_id: data.document_id || undefined,
        notes: data.notes || '',
      };
      return shipmentsApi.createShipment(request);
    },
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', t('shipments.messages.created'));
      setIsDialogOpen(false);
      setFormData(emptyForm);
      setFormErrors({});
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error || error.message;
      showAlert('error', `${t('shipments.messages.createError')}: ${msg}`, 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => shipmentsApi.deleteShipment(id),
    onSuccess: () => {
      pagination.refresh();
      showAlert('success', t('shipments.messages.deleted'));
      setDeleteDialogOpen(false);
      setSelectedShipment(null);
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error || error.message;
      showAlert('error', `${t('shipments.messages.deleteError')}: ${msg}`, 5000);
    },
  });

  const addEventMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CreateTrackingEventRequest }) =>
      shipmentsApi.addTrackingEvent(id, data),
    onSuccess: () => {
      showAlert('success', t('shipments.messages.eventAdded'));
      setAddEventDialogOpen(false);
      setNewEvent({ description: '', location: '', status: '' });
      if (trackingShipment) {
        loadTrackingDetail(trackingShipment.id);
      }
      pagination.refresh();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error || error.message;
      showAlert('error', `${t('shipments.messages.eventError')}: ${msg}`, 5000);
    },
  });

  const loadTrackingDetail = async (id: number) => {
    try {
      const detail = await shipmentsApi.getShipment(id);
      let docNumber: string | null = null;
      if (detail.document) {
        try {
          const doc = await documentsApi.getDocument(detail.document);
          docNumber = doc.document_number;
        } catch {
          // ignore – fallback to "Dokument #id"
        }
      }
      setTrackingShipment(detail);
      setWzDocumentNumber(docNumber);
      setTrackingDialogOpen(true);
    } catch {
      showAlert('error', t('shipments.messages.fetchError'), 5000);
    }
  };

  const handleRowClick = (row: ShipmentListItem) => {
    loadTrackingDetail(row.id);
  };

  const handleFormChange = (field: keyof ShipmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCreateSubmit = () => {
    const errors: Record<string, string> = {};
    if (!formData.sender.trim()) errors.sender = t('shipments.form.requiredField');
    if (!formData.receiver.trim()) errors.receiver = t('shipments.form.requiredField');
    if (!formData.status) errors.status = t('shipments.form.requiredField');
    const w = Number(formData.weight);
    if (isNaN(w) || w < 0) errors.weight = t('shipments.form.invalidWeight');

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    createMutation.mutate(formData);
  };

  const columns: Column<ShipmentListItem>[] = [
    {
      id: 'tracking_number',
      label: t('shipments.columns.trackingNumber'),
      flex: 1.5,
    },
    { id: 'sender', label: t('shipments.columns.sender'), flex: 1.5 },
    { id: 'receiver', label: t('shipments.columns.receiver'), flex: 1.5 },
    { id: 'shipment_date', label: t('shipments.columns.shipmentDate'), flex: 1 },
    {
      id: 'status',
      label: t('shipments.columns.status'),
      flex: 1,
      format: (_value: string, row: ShipmentListItem) => (
        <Chip
          label={row.status_display}
          color={statusColorMap[row.status] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'carrier',
      label: t('shipments.columns.carrier'),
      flex: 0.8,
      format: (_value: string, row: ShipmentListItem) => row.carrier_display,
    },
    {
      id: 'weight',
      label: t('shipments.columns.weight'),
      flex: 0.6,
      align: 'right',
    },
    {
      id: 'tracking_events_count',
      label: '',
      flex: 0.8,
      format: (_value: any, row: ShipmentListItem) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={t('shipments.viewTracking')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                loadTrackingDetail(row.id);
              }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('shipments.deleteShipment')}>
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedShipment(row);
                setDeleteDialogOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const formatTimestamp = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString('pl-PL', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const selectedWzDoc = wzDocuments.find(d => d.id === formData.document_id) || null;

  return (
    <Box>
      <Table
        columns={columns}
        data={pagination.data}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalRows={pagination.totalCount}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handlePageSizeChange}
        onSearchChange={pagination.handleSearchChange}
        loading={pagination.isLoading}
        rowKey="id"
        title={t('shipments.title')}
        description={t('shipments.description')}
        onAddButtonClick={() => {
          setFormData(emptyForm);
          setFormErrors({});
          setIsDialogOpen(true);
        }}
        addButtonText={t('shipments.newShipment')}
        searchable={true}
        searchPlaceholder={t('shipments.searchPlaceholder')}
        onRowClick={handleRowClick}
      />

      {/* ============ CREATE SHIPMENT DIALOG ============ */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '11px',
            minWidth: 400,
            background: '#2a2a2a',
            border: '1px solid #474747ff',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {t('shipments.addNewShipment')}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setIsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {/* Sender */}
            <TextField
              label={t('shipments.form.sender')}
              value={formData.sender}
              onChange={e => handleFormChange('sender', e.target.value)}
              required
              error={!!formErrors.sender}
              helperText={formErrors.sender}
              fullWidth
            />

            {/* Receiver */}
            <TextField
              label={t('shipments.form.receiver')}
              value={formData.receiver}
              onChange={e => handleFormChange('receiver', e.target.value)}
              required
              error={!!formErrors.receiver}
              helperText={formErrors.receiver}
              fullWidth
            />

            {/* Status - SELECT */}
            <TextField
              select
              label={t('shipments.form.status')}
              value={formData.status}
              onChange={e => handleFormChange('status', e.target.value)}
              required
              error={!!formErrors.status}
              helperText={formErrors.status}
              fullWidth
            >
              <MenuItem value="w_magazynie">{t('shipments.statuses.w_magazynie')}</MenuItem>
              <MenuItem value="w_drodze">{t('shipments.statuses.w_drodze')}</MenuItem>
              <MenuItem value="dostarczona">{t('shipments.statuses.dostarczona')}</MenuItem>
              <MenuItem value="anulowana">{t('shipments.statuses.anulowana')}</MenuItem>
            </TextField>

            {/* Weight */}
            <TextField
              label={t('shipments.form.weight')}
              value={formData.weight}
              onChange={e => handleFormChange('weight', e.target.value)}
              required
              type="number"
              inputProps={{ min: 0, step: 0.1 }}
              error={!!formErrors.weight}
              helperText={formErrors.weight}
              fullWidth
            />

            {/* Carrier - SELECT */}
            <TextField
              select
              label={t('shipments.form.carrier')}
              value={formData.carrier}
              onChange={e => handleFormChange('carrier', e.target.value)}
              fullWidth
            >
              <MenuItem value="manual">{t('shipments.carriers.manual')}</MenuItem>
              <MenuItem value="inpost">InPost</MenuItem>
              <MenuItem value="fedex">FedEx</MenuItem>
              <MenuItem value="dhl">DHL</MenuItem>
              <MenuItem value="dpd">DPD</MenuItem>
              <MenuItem value="ups">UPS</MenuItem>
              <MenuItem value="other">{t('shipments.carriers.other')}</MenuItem>
            </TextField>

            {/* Carrier tracking number */}
            <TextField
              label={t('shipments.form.carrierTrackingNumber')}
              value={formData.carrier_tracking_number}
              onChange={e => handleFormChange('carrier_tracking_number', e.target.value)}
              fullWidth
              placeholder={t('shipments.form.carrierTrackingPlaceholder')}
            />

            {/* Document WZ - AUTOCOMPLETE */}
            <Autocomplete
              options={wzDocuments}
              value={selectedWzDoc}
              onChange={(_e, newValue) => handleFormChange('document_id', newValue?.id || null)}
              getOptionLabel={(opt) => `${opt.document_number} — ${opt.client_name || opt.supplier_name || ''}`}
              loading={wzLoading}
              sx={getAutocompleteStyles()}
              ListboxProps={{
                sx: getAutocompleteListboxStyles(),
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('shipments.form.documentWZ')}
                  placeholder={t('shipments.form.documentWZPlaceholder')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {wzLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText={t('shipments.form.noDocuments')}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.document_number}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.client_name || option.supplier_name || '—'} · {option.document_status_display}
                    </Typography>
                  </Box>
                </li>
              )}
            />

            {/* Notes */}
            <TextField
              label={t('shipments.form.notes')}
              value={formData.notes}
              onChange={e => handleFormChange('notes', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setIsDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 100,
              borderColor: '#474747ff',
              color: 'text.primary',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.05)' },
            }}
          >
            {t('shipments.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateSubmit}
            disabled={createMutation.isPending}
            sx={{ minWidth: 100 }}
          >
            {t('shipments.addShipment')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ DELETE CONFIRMATION ============ */}
      <CustomPopup
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setSelectedShipment(null); }}
        title={t('shipments.deleteShipment')}
        text={`${t('shipments.confirmDelete')} ${selectedShipment?.tracking_number || ''}?`}
        confirmText={t('shipments.delete')}
        cancelText={t('shipments.cancel')}
        onConfirm={() => selectedShipment && deleteMutation.mutate(selectedShipment.id)}
        confirmButtonColor="error"
      />

      {/* ============ TRACKING DETAIL DIALOG ============ */}
      <Dialog
        open={trackingDialogOpen}
        onClose={() => { setTrackingDialogOpen(false); setTrackingShipment(null); setWzDocumentNumber(null); }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '11px',
            background: '#2a2a2a',
            border: '1px solid #474747ff',
            minHeight: 400,
          },
        }}
      >
        {trackingShipment && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
              <ShippingIcon color="primary" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="span">
                  {trackingShipment.tracking_number}
                </Typography>
                <Chip
                  label={trackingShipment.status_display}
                  color={statusColorMap[trackingShipment.status] || 'default'}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </Box>
              <IconButton edge="end" color="inherit" onClick={() => { setTrackingDialogOpen(false); setTrackingShipment(null); setWzDocumentNumber(null); }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers>
              {/* Shipment info grid */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 3 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shipments.columns.sender')}</Typography>
                  <Typography variant="body2">{trackingShipment.sender}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shipments.columns.receiver')}</Typography>
                  <Typography variant="body2">{trackingShipment.receiver}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shipments.columns.weight')}</Typography>
                  <Typography variant="body2">{trackingShipment.weight} kg</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shipments.columns.shipmentDate')}</Typography>
                  <Typography variant="body2">{trackingShipment.shipment_date}</Typography>
                </Box>
                {trackingShipment.delivery_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('shipments.deliveryDate')}</Typography>
                    <Typography variant="body2">{trackingShipment.delivery_date}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('shipments.columns.carrier')}</Typography>
                  <Typography variant="body2">
                    {trackingShipment.carrier_display}
                    {trackingShipment.carrier_tracking_number && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        ({trackingShipment.carrier_tracking_number})
                      </Typography>
                    )}
                  </Typography>
                </Box>
                {trackingShipment.document && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('shipments.form.documentWZ')}</Typography>
                    <Typography variant="body2">
                      {wzDocumentNumber ?? t('shipments.documentLinked', { id: trackingShipment.document })}
                    </Typography>
                  </Box>
                )}
              </Box>

              {trackingShipment.notes && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" color="text.secondary">{t('shipments.form.notes')}</Typography>
                  <Typography variant="body2">{trackingShipment.notes}</Typography>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Tracking timeline */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {t('shipments.trackingHistory')}
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setNewEvent({ description: '', location: '', status: trackingShipment.status });
                    setAddEventDialogOpen(true);
                  }}
                >
                  {t('shipments.addEvent')}
                </Button>
              </Box>

              {trackingShipment.tracking_events && trackingShipment.tracking_events.length > 0 ? (
                <Stepper orientation="vertical" activeStep={-1} sx={{ ml: 1 }}>
                  {trackingShipment.tracking_events.map((event: TrackingEvent, index: number) => (
                    <Step key={event.id} active expanded>
                      <StepLabel
                        icon={
                          <Chip
                            label={event.status_display}
                            color={statusColorMap[event.status] || 'default'}
                            size="small"
                            variant={index === 0 ? 'filled' : 'outlined'}
                          />
                        }
                      >
                        <Typography variant="body2" fontWeight={index === 0 ? 'bold' : 'normal'}>
                          {event.description}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(event.timestamp)}
                          </Typography>
                          {event.location && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                              <PlaceIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {event.location}
                              </Typography>
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {event.source_display}
                            {event.created_by_name && ` · ${event.created_by_name}`}
                          </Typography>
                        </Box>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {t('shipments.noTrackingEvents')}
                </Typography>
              )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => { setTrackingDialogOpen(false); setTrackingShipment(null); setWzDocumentNumber(null); }}
              >
                {t('shipments.close')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ============ ADD TRACKING EVENT DIALOG ============ */}
      <Dialog
        open={addEventDialogOpen}
        onClose={() => setAddEventDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '11px',
            background: '#2a2a2a',
            border: '1px solid #474747ff',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {t('shipments.addEvent')}
            </Typography>
            <IconButton edge="end" color="inherit" onClick={() => setAddEventDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={t('shipments.eventForm.description')}
              value={newEvent.description}
              onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
              required
              multiline
              rows={2}
              placeholder={t('shipments.eventForm.descriptionPlaceholder')}
              fullWidth
            />
            <TextField
              label={t('shipments.eventForm.location')}
              value={newEvent.location}
              onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
              placeholder={t('shipments.eventForm.locationPlaceholder')}
              fullWidth
            />
            <TextField
              select
              label={t('shipments.eventForm.status')}
              value={newEvent.status || ''}
              onChange={(e) => setNewEvent({ ...newEvent, status: e.target.value })}
              fullWidth
            >
              <MenuItem value="">{t('shipments.eventForm.noStatusChange')}</MenuItem>
              <MenuItem value="w_magazynie">{t('shipments.statuses.w_magazynie')}</MenuItem>
              <MenuItem value="w_drodze">{t('shipments.statuses.w_drodze')}</MenuItem>
              <MenuItem value="dostarczona">{t('shipments.statuses.dostarczona')}</MenuItem>
              <MenuItem value="anulowana">{t('shipments.statuses.anulowana')}</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setAddEventDialogOpen(false)}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 100,
              borderColor: '#474747ff',
              color: 'text.primary',
              '&:hover': { borderColor: 'rgba(255,255,255,0.5)', backgroundColor: 'rgba(255,255,255,0.05)' },
            }}
          >
            {t('shipments.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!newEvent.description || addEventMutation.isPending}
            onClick={() => {
              if (trackingShipment) {
                const data: CreateTrackingEventRequest = {
                  description: newEvent.description,
                  location: newEvent.location || undefined,
                };
                if (newEvent.status) data.status = newEvent.status;
                addEventMutation.mutate({ id: trackingShipment.id, data });
              }
            }}
            sx={{ minWidth: 100 }}
          >
            {t('shipments.addEvent')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Shipments;
